/** THE setup page's server-state hook — a live view over the preflight checklist.
 *
 *  One items query (the Preflight tab's key, 4s poll while anything runs) plus
 *  the onboarding profile; from those it derives each step's status, the rail
 *  counts and the overall progress. There is no client-side wizard state to
 *  persist: leave the page mid-setup and the same URL re-derives everything.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import { items as itemsApi, onboardingProfile, type OnboardingProfile, type PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

import {
  SETUP_STEPS,
  parameterItems,
  type SetupStepDef,
  type SetupStepId,
  type SetupStepStatus,
} from './stepRegistry';

export interface SetupStep extends SetupStepDef {
  status: SetupStepStatus;
  /** One-line rail annotation, e.g. "3 required left" / "waiting on Catalog sync". */
  secondary?: string;
  visible: boolean;
}

export interface SetupState {
  items: PreflightItem[];
  itemsLoading: boolean;
  itemsError: boolean;
  profile: OnboardingProfile | undefined;
  steps: SetupStep[];
  /** Applicable-item completion for the header chip. */
  progress: { done: number; total: number };
  runReady: (itemIds?: number[]) => void;
  runReadyPending: boolean;
  setAutoAdvance: (enabled: boolean) => void;
  invalidate: () => void;
}

function itemStepStatus(owned: PreflightItem[]): SetupStepStatus {
  if (owned.some((i) => i.status === 'error')) return 'error';
  if (owned.some((i) => i.status === 'started')) return 'running';
  if (owned.every((i) => i.status === 'completed')) return 'done';
  const open = owned.filter((i) => i.status === 'pending' || i.status === 'error');
  // An OPTIONAL item missing its input is not a reason to flag the step: "Invite
  // team members" has nothing to invite on a tenant whose whole team is the admin
  // account, and that is a finished state, not an outstanding one.
  if (open.some((i) => !i.ready && !i.optional)) return 'attention';
  if (open.length > 0 && open.every((i) => i.blocked_by.length > 0)) return 'blocked';
  return 'ready';
}

function itemStepSecondary(owned: PreflightItem[], status: SetupStepStatus): string | undefined {
  if (status === 'blocked') {
    const blockers = new Set(owned.flatMap((i) => i.blocked_by.map((b) => b.action)));
    return `waiting on ${[...blockers].join(', ')}`;
  }
  if (status === 'attention') {
    // Counted the same way the status is decided, or the rail says "needs input"
    // for a step whose only unready item is optional.
    const missing = owned.filter((i) => !i.ready && !i.optional).length;
    return missing === 1 ? 'needs input' : `${missing} items need input`;
  }
  if (status === 'done' && owned.length > 1) return `${owned.length} items done`;
  return undefined;
}

export function useSetupState(topId: number): SetupState {
  const qc = useQueryClient();
  const snackbar = useSnackbar();

  const itemsQuery = useQuery({
    queryKey: ['preflight', 'items', topId],
    queryFn: () => itemsApi.list(topId),
    refetchInterval: (q) => ((q.state.data ?? []).some((i) => i.status === 'started') ? 4000 : false),
  });
  const profileQuery = useQuery({
    queryKey: ['preflight', 'onboarding-profile', topId],
    queryFn: () => onboardingProfile.get(topId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['preflight', 'items', topId] });
    void qc.invalidateQueries({ queryKey: ['preflight', 'onboarding-profile', topId] });
  };

  const runReadyMutation = useMutation({
    mutationFn: (itemIds?: number[]) =>
      itemsApi.runReady(topId, itemIds ? { item_ids: itemIds, arm: true } : { arm: true }),
    onSuccess: (resp) => {
      const waiting = resp.skipped.filter((s) => s.reason === 'not_ready').length;
      const blocked = resp.skipped.filter((s) => s.reason === 'blocked').length;
      const parts = [`${resp.dispatched.length} started`];
      if (waiting) parts.push(`${waiting} waiting on input`);
      if (blocked) parts.push(`${blocked} blocked (run when unblocked)`);
      snackbar.show(parts.join(' · '), resp.dispatched.length ? 'success' : 'info');
      invalidate();
    },
    onError: (e) => {
      const body = e instanceof ConsoleApiError ? e.body : null;
      snackbar.show(
        body && (body as { code?: string }).code === 'setup_run_disabled'
          ? 'Setup runs are not enabled in this environment.'
          : e instanceof ConsoleApiError
            ? e.message
            : 'Run setup failed.',
        'error',
      );
    },
  });

  const autoAdvanceMutation = useMutation({
    mutationFn: (enabled: boolean) => itemsApi.autoAdvance(topId, enabled),
    onSuccess: (resp) => {
      snackbar.show(resp.auto_advance ? 'Auto-advance resumed.' : 'Auto-advance paused.', 'info');
      invalidate();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Request failed.', 'error'),
  });

  const items = itemsQuery.data ?? [];
  const applicable = items.filter((i) => i.applicable);
  const progress = {
    done: applicable.filter((i) => i.status === 'completed').length,
    total: applicable.length,
  };

  const byAction = new Map(items.map((i) => [i.action, i]));
  const params = parameterItems(items);

  const steps: SetupStep[] = SETUP_STEPS.map((def) => {
    if (def.section === 'create') {
      // Create steps exist only in create mode; the setup page hides them
      // (identity lives on the tenant's General tab once it exists).
      return { ...def, status: 'done', visible: false };
    }
    if (def.id === 'profile') {
      return { ...def, status: 'edit', visible: true };
    }
    if (def.id === 'parameters') {
      const missing = params.filter((i) => i.status !== 'completed' && !i.ready).length;
      return {
        ...def,
        status: missing > 0 ? 'attention' : 'done',
        secondary: missing > 0 ? `${missing} required left` : undefined,
        visible: params.length > 0,
      };
    }
    if (def.id === 'run') {
      const required = applicable.filter((i) => !i.optional);
      const status: SetupStepStatus = applicable.some((i) => i.status === 'error')
        ? 'error'
        : applicable.some((i) => i.status === 'started')
          ? 'running'
          : required.length > 0 && required.every((i) => i.status === 'completed')
            ? 'done'
            : 'ready';
      return {
        ...def,
        status,
        secondary: `${progress.done} of ${progress.total} done`,
        visible: true,
      };
    }
    const owned = def.itemActions
      .map((a) => byAction.get(a))
      .filter((i): i is PreflightItem => Boolean(i && i.applicable));
    if (owned.length === 0) return { ...def, status: 'done', visible: false };
    const status = itemStepStatus(owned);
    return { ...def, status, secondary: itemStepSecondary(owned, status), visible: true };
  });

  return {
    items,
    itemsLoading: itemsQuery.isLoading,
    itemsError: Boolean(itemsQuery.error),
    profile: profileQuery.data,
    steps,
    progress,
    runReady: (itemIds) => runReadyMutation.mutate(itemIds),
    runReadyPending: runReadyMutation.isPending,
    setAutoAdvance: (enabled) => autoAdvanceMutation.mutate(enabled),
    invalidate,
  };
}

/** The step a fresh visit should land on: the first thing needing a human,
 *  else the run board. */
export function defaultStep(steps: SetupStep[]): SetupStepId {
  const first = steps.find(
    (s) => s.visible && (s.status === 'attention' || s.status === 'error' || s.status === 'ready'),
  );
  return (first?.id ?? 'run') as SetupStepId;
}
